/**
 * Gate hook for the readiness break-recommendation modal.
 *
 * Evaluates ALL preconditions from spec §11.1 and decides whether to show
 * the modal. Critical: this hook is wired to `trigger='step_submit'`
 * updates ONLY. Session-resume score updates refresh the HUD bars but
 * MUST NOT cause this gate to evaluate — otherwise a returning user with
 * a low pre-break score would see the modal the instant they reopen a
 * session, before doing anything.
 *
 * All thresholds are episode-scoped (since v1.4.x) — a user mid-episode
 * who starts a new lesson keeps their counters rather than restarting
 * them. Naming was clarified in v1.7.8 (was `MIN_SESSION_MINUTES` /
 * `sessionMinutes` even though the values were episode-relative).
 *
 * Algorithm v1.7.10: trigger threshold uses displayed `bars <= 2` instead
 * of `score < 0.4`. Same effective threshold (band 2 starts at score 0.2,
 * band 3 starts at 0.4 — so bars ≤ 2 ↔ score < 0.4 in steady state) BUT
 * the v1.7.9 block-on-correct rule can hold bars at 3 with a low score.
 * Triggering off bars keeps the modal consistent with the displayed
 * indicator: if the user sees 3 bars, the modal won't suggest a break.
 *
 * Preconditions (spec §11.1):
 *  - bars <= 2  (was score < 0.40 pre-v1.7.10)
 *  - episode has run >= MIN_EPISODE_MINUTES
 *  - >= MIN_RESPONSES step responses recorded in this episode
 *  - no modal shown in the last min(5 minutes, 5 step responses)
 *  - user is NOT in cold start
 *  - for learn sessions only: NOT within the last 2 steps of the session
 */
import { useMemo, useState } from 'react';

const MODAL_BARS_THRESHOLD = 2;
const MIN_EPISODE_MINUTES = 3;
const MIN_RESPONSES = 8;
const COOLDOWN_MIN_MS = 5 * 60 * 1000;
const LAST_N_STEPS_LEARN = 2;

export interface BreakGateInput {
  /** Latest readiness from the most recent step-submit response (NOT session-resume). */
  readiness:
    | {
        score: number;
        bars: 1 | 2 | 3 | 4 | 5;
        isColdStart: boolean;
      }
    | undefined;
  /** Total step-submit responses in this episode so far (episode-scoped). */
  responseCount: number;
  /**
   * Episode start timestamp (ms since epoch), or null if no in-session data
   * yet. Stable across renders — the gate computes elapsed minutes inside its
   * evaluation against `Date.now()` at gate-eval time, keeping the impure
   * read contained rather than recomputing every render in the parent.
   */
  episodeStartedAtMs: number | null;
  /** Session type — last-N-steps suppression applies to learn sessions only. */
  sessionType: 'learn' | 'revise';
  /**
   * Total number of steps in this session (questions AND info combined).
   * Used by the last-N-steps suppression rule, which is unit-matched against
   * `currentPosition` (also an all-steps index). Previously this field held
   * questionsTotal-only — a unit mismatch that caused `stepsRemaining` to go
   * negative on any lesson with info steps interleaved past the question count,
   * silently suppressing the modal for the entire back half of those lessons.
   */
  totalSteps: number;
  /** Current step position (0-indexed) across ALL steps (questions + info). */
  currentPosition: number;
  /**
   * Latest break-modal dismissal anywhere in the current episode (ms since
   * epoch), or null if never dismissed in this episode. Server-provided so
   * cooldown survives SessionPlayer remounts (e.g. starting a new lesson
   * within the same episode no longer resets the modal cooldown).
   */
  lastModalDismissedAtMs: number | null;
}

export interface BreakGateResult {
  showModal: boolean;
  dismissContinue: () => void;
  dismissBreak: () => void;
}

/**
 * Pure-ish gate predicate. `Date.now()` is read inside the memo callback (not
 * during render of the parent), so the impurity stays contained to gate
 * evaluation. Re-evaluates only when `input` or local dismissal state change.
 */
function passesAllGates(input: BreakGateInput): boolean {
  if (!input.readiness) return false; // session-resume update or no data — suppress
  if (input.readiness.isColdStart) return false; // cold-start floor (§11.3)
  if (input.readiness.bars > MODAL_BARS_THRESHOLD) return false;
  if (input.responseCount < MIN_RESPONSES) return false;
  if (input.episodeStartedAtMs == null) return false;

  // Date.now() is contained inside this gate predicate, called from inside a
  // useMemo. The rule's render-purity check doesn't follow the call here, and
  // the impurity is intentional — episode-elapsed and cooldown both need a
  // wall-clock read, and re-evaluation cadence is bounded by step-submit.
  const nowMs = Date.now();

  const episodeMinutes = (nowMs - input.episodeStartedAtMs) / 60_000;
  if (episodeMinutes < MIN_EPISODE_MINUTES) return false;

  // Cooldown: server-provided last-dismissal timestamp (episode-scoped).
  // Suppress if the modal was dismissed in the last COOLDOWN_MIN_MS.
  // Survives SessionPlayer remounts (e.g. starting a new lesson within the
  // same episode does not reset the modal cooldown).
  if (
    input.lastModalDismissedAtMs != null &&
    nowMs - input.lastModalDismissedAtMs < COOLDOWN_MIN_MS
  ) {
    return false;
  }

  // Last-N-steps-of-learn-session suppression. Both inputs are in all-steps
  // units — using `totalSteps` (was incorrectly `questionsTotal` before, a
  // unit mismatch with `currentPosition`).
  if (input.sessionType === 'learn') {
    const stepsRemaining = input.totalSteps - input.currentPosition;
    if (stepsRemaining <= LAST_N_STEPS_LEARN) return false;
  }

  return true;
}

/**
 * @param input — fresh state from each step-submit. Pass `undefined` for
 *                readiness to suppress the gate entirely (e.g. the latest
 *                update came from session-resume, not step-submit).
 * @param onTakeBreak — called when the user clicks "Take a break".
 *                      Caller is responsible for navigating to the home
 *                      screen / closing the session.
 */
export function useBreakModalGate(input: BreakGateInput, onTakeBreak: () => void): BreakGateResult {
  // Track which responseCount the user dismissed at, so the modal stays
  // suppressed for that exact step-submit but re-evaluates on the next one.
  // Replaces the previous useEffect+useRef idempotency pattern, which was a
  // setState-in-effect anti-pattern. The new model: showModal is a pure
  // derivation of (gate predicate over input) AND (no dismissal at this
  // responseCount), with dismissals updated only via event handlers.
  const [dismissedAtResponseCount, setDismissedAtResponseCount] = useState<number | null>(null);

  const showModal = useMemo(() => {
    if (dismissedAtResponseCount === input.responseCount) return false;
    return passesAllGates(input);
  }, [input, dismissedAtResponseCount]);

  const dismissContinue = () => {
    setDismissedAtResponseCount(input.responseCount);
    // Server-side persistence happens via the dismissal telemetry event
    // posted from ReadinessBreakModal (POST /me/readiness/events). The
    // study_sessions.readiness_modal_dismissed_at column is updated there;
    // the next step-submit response carries the new lastModalDismissedAtMs
    // back into the gate input and suppresses re-fires within COOLDOWN_MIN_MS.
  };

  const dismissBreak = () => {
    setDismissedAtResponseCount(input.responseCount);
    onTakeBreak();
  };

  return { showModal, dismissContinue, dismissBreak };
}
